/**
 * The Phase 1 -> Phase 2 bridge: deriving a freeform `details` blob from a
 * `FormField[]`.
 *
 * The legacy `live-session` path reads `interview_sessions.details` to build
 * hollie's prompt. The progressive path reads the immutable schema snapshot
 * directly. Every surface that authors a campaign from a `form_schema`
 * (the builder wizard AND the v1 API / MCP create path) must derive a `details`
 * blob from the schema when the author did not type one, or hollie would have no
 * questions to cover. Keeping this logic in one worker-safe module is what makes
 * an API-created campaign behave identically to a wizard-created one.
 *
 * This module is intentionally dependency-light (it imports only the `FormField`
 * type) so both the browser wizard (`services/interviewService.ts`) and the
 * `v1-api` worker can import it.
 *
 * @todo Phase 2: delete this module once `live-session` reads `form_schema`
 *       directly via dedicated `<must_cover_questions>` /
 *       `<try_to_cover_questions>` prompt blocks.
 *
 * @module lib/form-schema-details
 */
import type { FormField } from '../types';

/**
 * Derive a freeform `details` prompt blob from a `FormField[]`.
 *
 * Bullet rules:
 * - `free_form_explore` fields render as `- {label}` (or `- {label} - {topic}`
 *   when `topic` is set). They do NOT carry the type annotation, since the
 *   freeform style matches today's authoring shape.
 * - `information` blocks render an opaque positioned item without content,
 *   identifiers, or tool syntax. The live-session prompt carries the separate
 *   routing metadata, while the generic details list remains safe to speak.
 * - Choice fields include their exact option labels so hollie never invents a
 *   replacement scale or rejects a configured answer.
 * - All other field types render as `- Capture {label} ({type})`.
 *
 * @param fields The form schema authored in the builder canvas.
 * @returns A multi-line string suitable for `interview_sessions.details`.
 *          Only a genuinely empty schema (`fields.length === 0`) yields an empty
 *          string, so the always-re-derive edit path never overwrites a real
 *          value with nothing. A non-empty schema always derives a real blob,
 *          even when every field is a calendar block.
 */
export function deriveDetailsFromFormSchema(fields: FormField[]): string {
  // Only a genuinely empty schema yields an empty string. The edit path
  // (updateInterviewSessionConfig) always re-derives and persists this value, so
  // returning '' for a non-empty schema would wipe the previous topics blob and
  // leave the next session's prompt with no themes at all (finding 16).
  if (fields.length === 0) {
    return '';
  }

  // Calendar blocks are conversational behavior, not a topic: the live worker
  // builds their own booking prompt section from the block config, and the
  // generic fallback would emit a malformed "- Capture  (calendar_booking)"
  // bullet (the block has no label). When real topic fields exist they therefore
  // contribute no bullet at all.
  const topicFields = fields.filter((field) => field.type !== 'calendar_booking');

  if (topicFields.length === 0) {
    // A calendar-only schema is non-empty, so it must derive a real (non-empty)
    // blob. Emit one minimal bullet naming the booking step. It stays high-level
    // on purpose: the worker's dedicated calendar section drives the actual
    // booking behavior from the block config, so restating it here would only
    // duplicate that section.
    return [
      'Topics to cover during the interview:',
      '- Help the respondent book a meeting with the host when the moment is right.',
    ].join('\n');
  }

  const bullets = topicFields.map((field) => {
    if (field.type === 'free_form_explore') {
      const parts = [field.label];
      if (field.topic && field.topic.trim().length > 0) {
        parts.push(field.topic);
      }
      return `- ${parts.join(' - ')}`;
    }
    if (field.type === 'information') {
      if (field.responseMode === 'passive') return field.contentVariant === 'text' ? `- Information: ${field.body ?? ''}` : '- Passive image content at this position. Its content is supplied separately by the live session.';
      // Preserve schema position without embedding internal syntax that a live
      // model could recite instead of executing.
      return '- Required visual reaction at this exact position. Its content is supplied separately by the live session.';
    }
    if (field.type === 'choice_single' || field.type === 'choice_multi') {
      const optionLabels = field.options
        .map((option) => option.label.trim())
        .filter((label) => label.length > 0);
      if (optionLabels.length > 0) {
        return `- Capture ${field.label} (${field.type}; exact options: ${optionLabels.join(' | ')}${field.otherOptionId ? '; the Other option accepts a specified reason, kept together with its selection' : ''})`;
      }
    }
    return `- Capture ${field.label} (${field.type})`;
  });

  return ['Topics to cover during the interview:', ...bullets].join('\n');
}

/**
 * Pick the value to send for the RPC's `p_details` argument. Explicit non-empty
 * `details` wins; otherwise derive from `form_schema` when non-empty; otherwise
 * fall through to the original (possibly null) value.
 *
 * @param explicit The author-typed details, if any.
 * @param formSchema The campaign's question structure.
 * @returns The details string to persist, or null when there is nothing to say.
 */
export function resolveDetails(explicit: string | undefined, formSchema: FormField[]): string | null {
  if (typeof explicit === 'string' && explicit.trim().length > 0) {
    return explicit;
  }
  if (formSchema.length > 0) {
    return deriveDetailsFromFormSchema(formSchema);
  }
  return explicit ?? null;
}
